// FileWidget.js
// One `kind: 'file'` manifest entry rendered inline. A file a function produced
// — a rendered spreadsheet, a PDF, an image — is far more useful shown than
// offered as a download link, so the card shows the artifact itself with a
// download button in a bar beneath it. The run result's tab strip renders this same
// card for a file tab, handing in its bytes cache so a tab shown again is not
// downloaded again.

import React, { useRef, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FileViewer from '../../common/FileViewer';

function FileWidget({ entry, bytesCache = null }) {
  const viewerRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  // The viewer already holds the fetched bytes, so downloading goes through it
  // rather than signing and fetching the object a second time.
  const handleDownload = async () => {
    if (!viewerRef.current?.download) return;
    try {
      setDownloading(true);
      await viewerRef.current.download();
    } catch (err) {
      console.error('Failed to download widget file:', err);
    } finally {
      setDownloading(false);
    }
  };

  // FileViewer fills its container with `flex: 1`, so the container has to be a
  // flex column or the viewer collapses to nothing.
  return (
    <Box sx={{ height: '100%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <FileViewer
        ref={viewerRef}
        file={{
          gcsPath: entry?.gcs_path,
          filename: entry?.filename || entry?.key,
          contentType: entry?.mime,
        }}
        bytesCache={bytesCache}
      />
      {/* The download sits in a bar below the file, as a data table's export
          does. Laid over the file, it was drawn on the workbook's white formula
          bar, where the dark theme's white icon could not be seen. */}
      <Box sx={{ display: 'flex', alignItems: 'center', padding: '8px', flexShrink: 0 }}>
        <Tooltip title="Download">
          <span>
            <IconButton
              onClick={handleDownload}
              disabled={downloading}
              size="medium"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                ml: 1,
                borderRadius: '8px',
                padding: '8px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: '#4E85EB',
                  border: '1px solid #4E85EB',
                  backgroundColor: 'rgba(78, 133, 235, 0.1)',
                },
              }}
            >
              <DownloadIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default FileWidget;
